import React, { useState } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthProvider } from '../../auth/hooks/auth-provider';
import { useProject, usePatchProject, useUpdateProjectStatus } from '../../hooks/useProject';
import ProjectForm from '../../components/project/ProjectForm';
import { ProjectUpdateRequest, ProjectStatus, ProjectCreateRequest } from '../../models/Project';
import { UserType } from '../../models/UserType';
import { createProjectPatchOperations } from '../../utils/projectPatchUtils';

const ProjectEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const projectId = parseInt(id || '0');

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthProvider();

  const { data: project, isLoading, error } = useProject(projectId);
  const patchProjectMutation = usePatchProject();
  const updateStatusMutation = useUpdateProjectStatus();

  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');

  const handleUpdateProject = async (projectData: ProjectUpdateRequest, isDraft: boolean = false) => {
    try {
      if (!project) {
        throw new Error('Project not loaded');
      }

      // Create patch operations using the specialized function
      const patchOperations = createProjectPatchOperations(project, projectData);

      // Only update if there are changes
      if (patchOperations.length > 0) {
        await patchProjectMutation.mutateAsync({
          id: projectId,
          operations: patchOperations,
        });
      }

      // For draft projects, don't change status when just updating
      if (project.status === ProjectStatus.Draft && isDraft) {
        // Just update without changing status
        setAlertSeverity('success');
        setAlertMessage(t('project.draftUpdated'));
        setAlertOpen(true);
      } else if (project.status === ProjectStatus.Draft && !isDraft) {
        // Draft project being submitted - change status to Submitted
        await updateStatusMutation.mutateAsync({
          id: projectId,
          statusUpdate: {
            status: ProjectStatus.Submitted,
          },
        });

        setAlertSeverity('success');
        setAlertMessage(t('project.projectSubmitted'));
        setAlertOpen(true);
      } else {
        // Project is already in a status beyond Draft (Submitted, Proposed, etc.)
        // Just update the data without changing status
        setAlertSeverity('success');
        setAlertMessage(t('project.updatedSuccessfully'));
        setAlertOpen(true);
      }

      // Navigate back after short delay
      setTimeout(() => {
        navigate(`/projects/${projectId}`);
      }, 1500);
    } catch (error) {
      console.error('Error updating project:', error);
      setAlertSeverity('error');
      setAlertMessage(t('project.updateError'));
      setAlertOpen(true);
    }
  };

  const handleSubmitProject = async (projectData: ProjectCreateRequest) => {
    try {
      if (!project) {
        throw new Error('Project not loaded');
      }

      // First save the project data
      const patchOperations = createProjectPatchOperations(project, projectData);
      if (patchOperations.length > 0) {
        await patchProjectMutation.mutateAsync({
          id: projectId,
          operations: patchOperations,
        });
      }

      // Only update status to Submitted if project is currently in Draft
      if (project.status === ProjectStatus.Draft) {
        await updateStatusMutation.mutateAsync({
          id: projectId,
          statusUpdate: {
            status: ProjectStatus.Submitted,
          },
        });

        setAlertSeverity('success');
        setAlertMessage(t('project.projectSubmitted'));
        setAlertOpen(true);
      } else {
        // Project is already beyond Draft, just update data
        setAlertSeverity('success');
        setAlertMessage(t('project.updatedSuccessfully'));
        setAlertOpen(true);
      }

      // Navigate back after short delay
      setTimeout(() => {
        navigate(`/projects/${projectId}`);
      }, 1500);
    } catch (error) {
      console.error('Error submitting project:', error);
      setAlertSeverity('error');
      setAlertMessage(t('project.submitError'));
      setAlertOpen(true);
    }
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const handleNavigateToDetails = () => {
    navigate(`/projects/${projectId}`);
  };

  const handleNavigateToList = () => {
    if (user.type === UserType.Client) {
      navigate('/projects');
    } else {
      navigate('/projects/all');
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Box p={3}>
        <Typography color="error">
          {t('project.errorLoadingProject')}: {error?.message || t('project.notFound')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h2">{t('project.editProject')}</Typography>
      </Box>

      <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={handleNavigateToList}
          sx={{ cursor: 'pointer' }}
        >
          {user.type === UserType.Client ? t('project.myProjects') : t('project.projectList')}
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={handleNavigateToDetails}
          sx={{ cursor: 'pointer' }}
        >
          {project.name}
        </Link>
        <Typography color="text.primary">{t('common.edit')}</Typography>
      </Breadcrumbs>

      <ProjectForm
        initialData={project}
        onSubmit={handleUpdateProject}
        onSubmitProject={handleSubmitProject}
        isSubmitting={patchProjectMutation.isPending || updateStatusMutation.isPending}
        submitButtonText={t('common.save')}
        isEdit={true}
      />

      <Snackbar
        open={alertOpen}
        autoHideDuration={5000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleAlertClose}
          severity={alertSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectEdit;